import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, FileText, Plus, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Download, Send, Calculator, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_COLORS = { draft: "bg-gray-100 text-gray-700", submitted: "bg-blue-50 text-blue-700", approved: "bg-green-50 text-green-700", paid: "bg-green-50 text-green-700" };

export default function VATReturnsPage() {
  const { returnId } = useParams();
  if (returnId) return <VATReturnDetail returnId={returnId} />;
  return <VATReturnsList />;
}

function VATReturnsList() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  const fetchReturns = () => api.get("/vat/returns").then((r) => setReturns(r.data.returns || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { fetchReturns(); }, []);

  const generate = async () => {
    if (!periodStart || !periodEnd) { toast.error("Select period start and end dates"); return; }
    setGenerating(true);
    try {
      const r = await api.post("/vat/returns/generate", { period_start: periodStart, period_end: periodEnd });
      toast.success("VAT201 return generated");
      setGenOpen(false);
      fetchReturns();
      navigate(`/vat-returns/${r.data.return_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate"); }
    finally { setGenerating(false); }
  };

  // Suggest bi-monthly periods
  const suggestPeriods = () => {
    const now = new Date();
    const m = now.getMonth();
    const y = now.getFullYear();
    const startMonth = m % 2 === 0 ? m - 1 : m;
    const start = new Date(y, startMonth < 0 ? 11 : startMonth, 1);
    const end = new Date(y, startMonth < 0 ? 12 : startMonth + 2, 0);
    if (start.getFullYear() < y) start.setFullYear(y - 1);
    setPeriodStart(start.toISOString().split("T")[0]);
    setPeriodEnd(end.toISOString().split("T")[0]);
  };

  return (
    <div className="animate-fade-in" data-testid="vat-returns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">VAT Returns</h1>
          <p className="text-sm text-gray-500 mt-1">Generate and manage VAT201 returns for SARS</p>
        </div>
        <Button data-testid="generate-vat-return-btn" onClick={() => { setGenOpen(true); suggestPeriods(); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Generate VAT201
        </Button>
      </div>

      {loading ? <div className="animate-pulse h-64 bg-gray-100 rounded-sm" /> : returns.length === 0 ? (
        <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-10 text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No VAT returns generated yet. Click "Generate VAT201" to create your first return.</p>
        </CardContent></Card>
      ) : (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="vat-returns-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Return Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Period</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Output VAT</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Input VAT</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Payable / Refundable</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((r) => (
                  <tr key={r.return_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/vat-returns/${r.return_id}`)} data-testid={`vat-return-${r.return_id}`}>
                    <td className="px-4 py-3 font-mono font-medium text-[#E63946]">{r.return_number}</td>
                    <td className="px-4 py-3">{r.tax_period_start} to {r.tax_period_end}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(r.box_5_output_vat)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(r.box_16_input_vat)}</td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {r.box_17_total_vat_payable > 0 ? <span className="text-[#E63946]">{formatCurrency(r.box_17_total_vat_payable)} payable</span>
                        : <span className="text-green-600">{formatCurrency(r.box_18_total_vat_refundable)} refund</span>}
                    </td>
                    <td className="px-4 py-3 text-center"><Badge className={`${STATUS_COLORS[r.status]} rounded-full text-xs capitalize`}>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md" data-testid="generate-return-dialog">
          <DialogHeader><DialogTitle className="text-lg font-semibold">Generate VAT201 Return</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-[#374151] mb-1">Period Start</label><Input data-testid="period-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="rounded-sm border-gray-300 h-10" /></div>
            <div><label className="block text-sm font-medium text-[#374151] mb-1">Period End</label><Input data-testid="period-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="rounded-sm border-gray-300 h-10" /></div>
            <p className="text-xs text-gray-500">The system will calculate output VAT (sales) and input VAT (expenses) for this period.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-generate-btn" onClick={generate} disabled={generating} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {generating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating...</> : <><Calculator className="w-4 h-4 mr-1.5" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VATReturnDetail({ returnId }) {
  const [vr, setVr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/vat/returns/${returnId}`).then((r) => setVr(r.data)).catch(() => toast.error("Return not found")).finally(() => setLoading(false));
  }, [returnId]);

  const submit = async () => {
    if (!window.confirm("Submit this VAT return? This marks it as filed.")) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/vat/returns/${returnId}/submit`);
      setVr(r.data);
      toast.success("VAT return submitted");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!vr) return null;

  const isPayable = vr.box_17_total_vat_payable > 0;

  return (
    <div className="animate-fade-in max-w-4xl" data-testid="vat-return-detail">
      <Button variant="ghost" onClick={() => navigate("/vat-returns")} className="mb-4 text-gray-600"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Returns</Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{vr.return_number}</h1>
          <p className="text-sm text-gray-500">{vr.tax_period_start} to {vr.tax_period_end}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${STATUS_COLORS[vr.status]} rounded-full text-sm capitalize px-3 py-1`}>{vr.status}</Badge>
          {vr.status === "draft" && (
            <Button data-testid="submit-return-btn" onClick={submit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
              <Send className="w-4 h-4 mr-1.5" /> {submitting ? "Submitting..." : "Submit Return"}
            </Button>
          )}
        </div>
      </div>

      {/* Output VAT */}
      <Card className="border border-gray-200 rounded-sm shadow-sm mb-4">
        <CardContent className="p-5">
          <h2 className="font-heading font-semibold text-sm text-[#1F2937] mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#E63946]" /> Output Tax (VAT on Sales)</h2>
          <div className="space-y-2">
            <Row label="Box 1" desc="Standard Rate Supplies (15%)" value={vr.box_1_standard_rate_sales} />
            <Row label="Box 2" desc="Zero-Rated Supplies (0%)" value={vr.box_2_zero_rated_sales} />
            <Row label="Box 3" desc="Exempt Supplies" value={vr.box_3_exempt_sales} />
            <Row label="Box 4" desc="Total Sales (1+2+3)" value={vr.box_4_total_sales} bold />
            <Row label="Box 5" desc="Output Tax" value={vr.box_5_output_vat} bold highlight />
            <Row label="Box 8" desc="Exported Goods/Services" value={vr.box_8_export_sales} />
          </div>
        </CardContent>
      </Card>

      {/* Input VAT */}
      <Card className="border border-gray-200 rounded-sm shadow-sm mb-4">
        <CardContent className="p-5">
          <h2 className="font-heading font-semibold text-sm text-[#1F2937] mb-4 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-green-600" /> Input Tax (VAT on Purchases)</h2>
          <Row label="Box 16" desc="Input Tax Claimed" value={vr.box_16_input_vat} bold highlight />
        </CardContent>
      </Card>

      {/* Result */}
      <Card className={`border-2 ${isPayable ? "border-[#E63946]" : "border-green-500"} rounded-sm shadow-sm mb-4`}>
        <CardContent className="p-5">
          <h2 className="font-heading font-semibold text-sm text-[#1F2937] mb-4">Amount Payable or Refundable</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-sm">
              <div><span className="font-mono text-xs text-gray-500 mr-2">Box 17</span><span className="text-sm font-medium">Total VAT Payable (Box 5 - Box 16)</span></div>
              <span className="font-bold text-lg text-[#E63946]">{formatCurrency(vr.box_17_total_vat_payable)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-sm">
              <div><span className="font-mono text-xs text-gray-500 mr-2">Box 18</span><span className="text-sm font-medium">Total Refund Due (Box 16 - Box 5)</span></div>
              <span className="font-bold text-lg text-green-600">{formatCurrency(vr.box_18_total_vat_refundable)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supporting Info */}
      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <CardContent className="p-5">
          <h2 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Supporting Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-xs text-gray-500 uppercase mb-0.5">Invoices Included</p><p className="font-semibold">{vr.invoice_count}</p></div>
            <div><p className="text-xs text-gray-500 uppercase mb-0.5">Expenses Included</p><p className="font-semibold">{vr.expense_count}</p></div>
            <div><p className="text-xs text-gray-500 uppercase mb-0.5">Created By</p><p className="font-semibold">{vr.created_by_name || "-"}</p></div>
            <div><p className="text-xs text-gray-500 uppercase mb-0.5">Created</p><p className="font-semibold">{formatDate(vr.created_at)}</p></div>
          </div>
          {vr.submitted_date && (
            <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs text-gray-500 uppercase mb-0.5">Submitted By</p><p className="font-semibold">{vr.submitted_by_name || "-"}</p></div>
              <div><p className="text-xs text-gray-500 uppercase mb-0.5">Submitted Date</p><p className="font-semibold">{formatDate(vr.submitted_date)}</p></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, desc, value, bold, highlight }) {
  return (
    <div className={`flex items-center justify-between py-1.5 px-2 rounded-sm ${highlight ? "bg-gray-50" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-gray-400 w-12">{label}</span>
        <span className={`text-sm ${bold ? "font-semibold" : ""}`}>{desc}</span>
      </div>
      <span className={`text-sm ${bold ? "font-bold" : "font-medium"}`}>{formatCurrency(value || 0)}</span>
    </div>
  );
}
